import React, { useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Maximize2, Download, Loader2 } from "lucide-react"
import { FullscreenMediaModal, downloadMedia } from "@/components/shared/FullscreenMediaModal"
import { KatexRenderer } from "@/components/shared/KatexRenderer"

interface SvgDiagramRendererProps {
  content: string
  className?: string
  containerClassName?: string
  allowFullscreen?: boolean
  allowDownload?: boolean
  title?: string
  downloadFilename?: string
}

/**
 * Universal Diagram Component with Full Native SVG & ASCII/Markdown Support.
 * 
 * 1. For Native SVG / HTML: Directly mounts visualization markup with 100% fidelity,
 *    supporting CSS keyframe animations, <defs>, <use>, gradients, and infinite scaling.
 * 2. For ASCII / Unicode / Markdown diagrams: Automatically uses KatexRenderer to render
 *    clean monospace diagram canvases, GFM markdown tables, KaTeX math, and embedded imagery.
 * 3. Supports Fullscreen Modal and Multi-Format Downloads for all diagrams.
 */
export function SvgDiagramRenderer({
  content,
  className = "",
  containerClassName = "",
  allowFullscreen = false,
  allowDownload = false,
  title = "Interactive Diagram",
  downloadFilename,
}: SvgDiagramRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const rawHtml = useMemo(() => {
    if (!content || !content.trim()) return ""
    return content.trim()
  }, [content])

  const isSvgOrHtml = useMemo(() => {
    if (!rawHtml) return false
    return /<svg\b/i.test(rawHtml) || /<(?:div|span|table|section|article)\b/i.test(rawHtml)
  }, [rawHtml])

  const safeFilename = downloadFilename || title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "diagram"

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (downloading) return
    setDownloading(true)
    try {
      await downloadMedia({
        element: containerRef.current,
        filename: safeFilename,
      })
    } finally {
      setDownloading(false)
    }
  }

  if (!rawHtml) return null

  return (
    <div className={`relative group/svg-diagram ${containerClassName}`.trim()}>
      {/* Floating Action Controls */}
      {(allowDownload || allowFullscreen) && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5 opacity-100 sm:opacity-90 sm:group-hover/svg-diagram:opacity-100 transition-opacity">
          {allowDownload && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
              className="h-7 sm:h-6 px-2 gap-1 text-[11px] sm:text-[10px] font-semibold bg-card/90 hover:bg-card text-foreground border-border/70 shadow-xs backdrop-blur-xs"
              title="Download diagram image"
            >
              {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3 text-secondary" />}
              <span className="hidden sm:inline">{downloading ? "..." : "Download"}</span>
            </Button>
          )}

          {allowFullscreen && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                setFullscreenOpen(true)
              }}
              className="h-7 sm:h-6 px-2 gap-1 text-[11px] sm:text-[10px] font-semibold bg-card/90 hover:bg-card text-foreground border-border/70 shadow-xs backdrop-blur-xs"
              title="View Fullscreen"
            >
              <Maximize2 className="h-3.5 w-3.5 sm:h-3 sm:w-3 text-primary" />
              <span className="hidden sm:inline">Full Screen</span>
            </Button>
          )}
        </div>
      )}

      {/* Diagram Canvas: Dual Mode (SVG/HTML vs ASCII/Markdown) */}
      {isSvgOrHtml ? (
        <div
          ref={containerRef}
          onClick={() => {
            if (allowFullscreen && typeof window !== "undefined" && window.innerWidth < 640) {
              setFullscreenOpen(true)
            }
          }}
          className={`svg-diagram-wrapper overflow-x-auto rounded-xl max-w-full cursor-pointer sm:cursor-default ${className}`.trim()}
          dangerouslySetInnerHTML={{ __html: rawHtml }}
        />
      ) : (
        <div
          ref={containerRef}
          onClick={() => {
            if (allowFullscreen && typeof window !== "undefined" && window.innerWidth < 640) {
              setFullscreenOpen(true)
            }
          }}
          className={`ascii-diagram-wrapper overflow-x-auto rounded-xl max-w-full cursor-pointer sm:cursor-default ${className}`.trim()}
        >
          <KatexRenderer content={rawHtml} />
        </div>
      )}

      {/* Mobile Hint: Tap to expand */}
      {allowFullscreen && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setFullscreenOpen(true)
          }}
          className="sm:hidden w-full flex items-center justify-center gap-1.5 pt-2 pb-0.5 text-muted-foreground hover:text-foreground text-[11px] font-medium cursor-pointer transition-colors"
        >
          <Maximize2 className="h-3 w-3 text-primary" />
          <span>Tap to expand full screen & zoom</span>
        </button>
      )}

      {/* Fullscreen Modal View */}
      {allowFullscreen && (
        <FullscreenMediaModal
          isOpen={fullscreenOpen}
          onClose={() => setFullscreenOpen(false)}
          title={title}
          allowDownload={allowDownload}
          downloadFilename={safeFilename}
        >
          {isSvgOrHtml ? (
            <div
              className="w-full flex justify-center items-center py-4"
              dangerouslySetInnerHTML={{ __html: rawHtml }}
            />
          ) : (
            <div className="w-full max-w-4xl p-4 sm:p-6 bg-card rounded-xl border border-border/60 overflow-x-auto shadow-sm">
              <KatexRenderer content={rawHtml} />
            </div>
          )}
        </FullscreenMediaModal>
      )}
    </div>
  )
}
